import { axios } from '@vyce/core/src/api';

import config from '../config';
import {
  CreateSite,
  DailySchedule,
  EmployeeActivityLog,
  LocationUserDTO,
  OnSiteTradesData,
  RecentlyRegisteredDTO,
  Site,
  SiteShift,
  SiteShiftRule,
  TimeLogByPeriod,
  TimeLogByShift,
  TimeLogBySite,
  TimeLogByWorker,
  TimeOrderByItem,
  TimePeriodSettings,
  TimeTotalInfoProps,
  TopPerformingTeamMembersLatWeekDTO,
  TrackEventType,
  WorkersWorkedTimeOverview,
  ClockOutOutsideSettings,
} from '../../types';
import {
  ActiveCheckInDTO,
  AddSiteWorkerRequest,
  AddTimePeriodPayload,
  BulkAmendTimeLogsRequest,
  BulkEditTimeLogsRequestPayload,
  CheckInWorkerDTO,
  CheckInWorkersRequestProps,
  CheckOutWorkerDTO,
  CheckOutWorkersRequestProps,
  ClockedOutRolesDataRequest,
  CreateShiftRequest,
  CustomLocationField,
  CustomLocationFieldsDetailsDTO,
  CustomLocationFieldsDTO,
  EditWorkerHoursRequestProps,
  EmployeeLocationsRequest,
  ExportTimeLogsRequestData,
  GetEventsRequest,
  GetEventsRequestPayload,
  GetSitesPayload,
  GetSiteWorkersRequest,
  GetSiteWorkersSearchRequestPayload,
  GetSyncSettingsData,
  GetTimeLogsPayload,
  GetTimePeriodsRequest,
  LocationFieldDetailsDTO,
  LocationUsersBarchart,
  ManualClockInOut,
  ModuleCandidatatesSchema,
  ReportSettingsDTO,
  ShiftRuleSchema,
  TeamMembersWithoutRateDTO,
  TimeLogDTO,
  TimeLogsByWorkerRequest,
  TimeLogsRequestProps,
  TimeLogTotal,
  UpdatedReportSettingsData,
  UpdateShiftRequest,
  UpdateSiteRequest,
  WorkerLookupDTO,
} from '../types';
import dayjs from 'dayjs';

const URL = `${config.timeApiUrl}`;

export const createSiteRequest = (companyId: string, site: CreateSite) => {
  return axios.post(`${URL}/companies/${companyId}/sites`, site);
};

export const getSitesRequest = (companyId: string, data: GetSitesPayload) => {
  return axios.post(`${URL}/companies/${companyId}/sites/search`, data);
};

export const getSiteRequest = (companyId: string, siteId: string) => {
  return axios.get(`${URL}/companies/${companyId}/sites/${siteId}`);
};

export const deleteSiteRequest = (companyId: string, siteId: string) => {
  return axios.delete(`${URL}/companies/${companyId}/sites/${siteId}/remove`);
};

export const updateSiteRequest = ({ companyId, siteId, site }: UpdateSiteRequest) => {
  return axios.patch(`${URL}/companies/${companyId}/sites/${siteId}`, site);
};

export const createShiftRequest = ({ companyId, shift, siteId }: CreateShiftRequest) => {
  return axios.post(`${URL}/companies/${companyId}/sites/${siteId}/shifts`, shift);
};

export const updateShiftRequest = ({ companyId, siteId, shiftId, shift }: UpdateShiftRequest) => {
  return axios.patch(`${URL}/companies/${companyId}/sites/${siteId}/shifts/${shiftId}`, shift);
};

export const updateAllRulesShiftRequest = ({
  companyId,
  rules,
}: {
  companyId: string;
  rules: { rules: SiteShiftRule[] };
}) => {
  return axios.post(`${URL}/companies/${companyId}/sites/shifts/rules`, rules);
};

export const deleteShiftRequest = (companyId: string, siteId: string, shiftId: string) => {
  return axios.delete(`${URL}/companies/${companyId}/sites/${siteId}/shifts/${shiftId}`);
};

export const addSiteWorkerRequest = ({ companyId, shiftId, siteId, worker }: AddSiteWorkerRequest) => {
  return axios.put(`${URL}/companies/${companyId}/sites/${siteId}/shifts/${shiftId}/workers`, worker);
};

export const updateSiteWorkerRequest = ({ companyId, shiftId, siteId, worker }: AddSiteWorkerRequest) => {
  return axios.patch(
    `${URL}/companies/${companyId}/sites/${siteId}/shifts/${shiftId}/workers/${worker.user_id}`,
    worker
  );
};

export const getSiteWorkersRequest = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: GetSiteWorkersSearchRequestPayload;
}) => {
  return axios.post(`${URL}/companies/${companyId}/sites/workers/search`, payload);
};

export const getWorkerSitesRequest = ({
  companyId,
  userId,
  payload,
}: GetSiteWorkersRequest & { userId: string }) => {
  return axios.post<{ items: Site[]; count: number }>(
    `${URL}/companies/${companyId}/sites/users/${userId}/search`,
    { ...payload, show_deleted: false, only_active: true }
  );
};

export const deleteShiftWorkerRequest = (
  companyId: string,
  siteId: string,
  shiftId: string,
  workerId: string
) => {
  return axios.delete(`${URL}/companies/${companyId}/sites/${siteId}/shifts/${shiftId}/workers/${workerId}`);
};

export const deleteWorkerFromLocationRequest = (companyId: string, siteId: string, workerId: string) => {
  return axios.delete(`${URL}/companies/${companyId}/sites/${siteId}/workers/${workerId}`);
};

export const deleteAllShiftsWorkerRequest = (companyId: string, workerId: string) => {
  return axios.delete(`${URL}/companies/${companyId}/sites/workers/${workerId}`);
};

export const getActivityLogEventsRequest = ({ companyId, payload }: GetEventsRequest) => {
  return axios.post(`${URL}/monitoring/companies/${companyId}/activity_log`, payload);
};

export const getTimePeriodsRequest = ({ companyId, payload }: GetTimePeriodsRequest) => {
  return axios.post(`${URL}/time_periods/companies/${companyId}/time_periods/search`, payload);
};

export const getEventsRequest = ({ companyId, payload }: GetEventsRequest) => {
  return axios.post(`${URL}/companies/${companyId}/events/search`, payload);
};

export const getMapViewEventsRequest = ({
  companyId,
  siteId,
  eventType,
}: {
  companyId: string;
  siteId?: string | null;
  eventType: TrackEventType;
}) => {
  const now = dayjs().utc(false);
  const gte = now.startOf('day');
  const lte = now.endOf('day');
  return axios.post(`${URL}/monitoring/companies/${companyId}/map_view`, {
    limit: 5000,
    site_id: siteId,
    time_period: {
      lte: lte.format(),
      gte: gte.format(),
    },
    event_type: eventType,
  });
};

export const manualClockInOutRequest = ({
  companyId,
  userId,
  payload,
}: {
  companyId: string;
  userId: string;
  payload: ManualClockInOut;
}) => {
  return axios.post(`${URL}/companies/${companyId}/users/${userId}/checks`, payload);
};

export const checkUserActiveCheckInRequest = ({
  companyId,
  userId,
}: {
  companyId: string;
  userId: string;
}) => {
  return axios.post<ActiveCheckInDTO>(
    `${URL}/monitoring/companies/${companyId}/users/${userId}/active_check_in`
  );
};

export const getLookupSiteWorkersRequest = ({ companyId, payload }: GetSiteWorkersRequest) => {
  return axios.post<{ count: number; items: WorkerLookupDTO[] }>(
    `${URL}/companies/${companyId}/sites/workers/lookup`,
    payload
  );
};

export const getLocationManagers = ({
  companyId,
  siteId,
  payload,
}: {
  companyId: string;
  siteId: string;
  payload: {
    order_by: {
      field: string;
      desc: boolean;
    }[];
  };
}) => {
  return axios.post<{
    count: number;
    items: {
      user_id: string;
      first_name: string;
      last_name: string;
      avatar: string;
    }[];
  }>(`${URL}/companies/${companyId}/sites/sites/${siteId}/managers`, payload);
};
export const appointLocationManager = ({
  companyId,
  siteId,
  userId,
}: {
  companyId: string;
  siteId: string;
  userId: string;
}) => {
  return axios.put(`${URL}/companies/${companyId}/sites/sites/${siteId}/managers/${userId}`);
};

export const deleteLocationManager = ({
  companyId,
  siteId,
  userId,
}: {
  companyId: string;
  siteId: string;
  userId: string;
}) => {
  return axios.delete(`${URL}/companies/${companyId}/sites/sites/${siteId}/managers/${userId}`);
};

export const getCheckInWorkersRequest = ({
  companyId,
  payload,
  signal,
}: {
  companyId: string;
  payload: CheckInWorkersRequestProps;
  signal?: AbortSignal;
}) => {
  return axios.post<{ count: number; items: CheckInWorkerDTO[] }>(
    `${URL}/monitoring/companies/${companyId}/workers/checked_in`,
    payload,
    { signal }
  );
};

export const getCheckInWorkersRolesRequest = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: {
    limit: number;
    offset: number;
  };
}) => {
  return axios.post<{ items: string[] }>(
    `${URL}/monitoring/companies/${companyId}/workers/checked_in/roles`,
    payload
  );
};

export const getCheckOutWorkersRolesRequest = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: {
    limit: number;
    offset: number;
  };
}) => {
  return axios.post<{ items: string[] }>(
    `${URL}/monitoring/companies/${companyId}/workers/checked_out/roles`,
    payload
  );
};

export const getCheckOutWorkersRequest = ({
  companyId,
  payload,
  signal,
}: {
  companyId: string;
  payload: CheckOutWorkersRequestProps;
  signal?: AbortSignal;
}) => {
  return axios.post<{ count: number; items: CheckOutWorkerDTO[] }>(
    `${URL}/monitoring/companies/${companyId}/workers/checked_out`,
    payload,
    { signal }
  );
};

export const getTimeLogsByPeriodRequest = (companyId: string, payload: { next_pivot_date: string }) => {
  return axios.post<{ count: number; items: TimeLogByPeriod[]; next_pivot_date: string }>(
    `${URL}/time_logs/companies/${companyId}/time_logs_by_period`,
    payload
  );
};

export const getTimeLogsByLocationRequest = (companyId: string, payload: TimeLogsRequestProps) => {
  return axios.post<{ count: number; items: TimeLogBySite[]; totals: TimeTotalInfoProps }>(
    `${URL}/time_logs/companies/${companyId}/time_logs_by_site`,
    payload
  );
};

export const getTimeLogsByWorkerRequest = (companyId: string, payload: TimeLogsByWorkerRequest) => {
  return axios.post<{ count: number; items: TimeLogByWorker[]; totals: TimeTotalInfoProps }>(
    `${URL}/time_logs/companies/${companyId}/time_logs_by_worker`,
    payload
  );
};

export const getTimeLogsByShiftRequest = (
  companyId: string,
  userId: string,
  payload: { site_id?: string; start_date: string; end_date: string }
) => {
  return axios.post<TimeLogByShift>(
    `${URL}/time_logs/companies/${companyId}/time_logs_by_worker_shift?user_id=${userId}`,
    payload
  );
};

export const approveByPeriodRequest = (companyId: string, payload: DailySchedule) => {
  return axios.post(`${URL}/time_logs/companies/${companyId}/periods/approval`, payload);
};

export const approveBySiteRequest = (companyId: string, siteId: string, payload: DailySchedule) => {
  return axios.post(`${URL}/time_logs/companies/${companyId}/periods/sites/${siteId}/approval`, payload);
};

export const approveByWorkerRequest = (
  companyId: string,
  siteId: string,
  userId: string,
  payload: DailySchedule
) => {
  return axios.post(
    `${URL}/time_logs/companies/${companyId}/periods/sites/${siteId}/users/${userId}/approval`,
    payload
  );
};

export const getTimeWorkerRequest = (userId: string, companyId: string = '') => {
  const query = companyId ? `?company_id=${companyId}` : '';
  return axios.get(`${URL}/users/${userId}${query}`);
};

export const editWorkerHoursRequest = ({
  companyId,
  day,
  amendments,
  userId,
  siteId,
  shiftId,
}: EditWorkerHoursRequestProps) => {
  return axios.post(
    `${URL}/time_logs/companies/${companyId}/periods/${day}/sites/${siteId}/shifts/${shiftId}/users/${userId}`,
    amendments
  );
};

export const postExportTimeLogsRequest = ({ companyId, data }: ExportTimeLogsRequestData) => {
  return axios.post(`${URL}/reports/companies/${companyId}/export_time_logs_to_xlsx`, data, {
    headers: {
      accept: 'application/json',
    },
    responseType: 'blob',
  });
};

export const setPeriodSettingsRequest = (companyId: string, payload: TimePeriodSettings) => {
  return axios.put(`${URL}/companies/${companyId}/period_settings`, payload);
};

export const getPeriodSettingsRequest = (companyId: string) => {
  return axios.get(`${URL}/companies/${companyId}/period_settings`);
};

export const postWorkersWorkedTimeOverviewRequest = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: { approved: boolean };
}) => {
  return axios.post<{ count: number; items: WorkersWorkedTimeOverview[] }>(
    `${URL}/time_logs/companies/${companyId}/widgets/workers_worked_time_overview`,
    payload
  );
};

export const getTopPerformingTeamMembersLatWeekRequest = ({ companyId }: { companyId: string }) => {
  return axios.get<TopPerformingTeamMembersLatWeekDTO>(
    `${URL}/time_logs/companies/${companyId}/widgets/top_performing_workers_last_week`
  );
};

export const getTimeLogsRequest = (userId: string, payload: GetTimeLogsPayload) => {
  return axios.post<{ count: number; items: TimeLogDTO[]; totals: TimeLogTotal }>(
    `${URL}/users/${userId}/time_logs/search`,
    payload
  );
};

export const getUserActivityLogEventsRequest = ({
  userId,
  payload,
}: {
  userId: string;
  payload: GetEventsRequestPayload;
}) => {
  return axios.post<{ items: EmployeeActivityLog[] }>(`${URL}/users/${userId}/activity_log`, payload);
};

export const addTimePeriodRequest = (companyId: string, payload: AddTimePeriodPayload) => {
  return axios.post<{ count: number; items: TimeLogDTO[]; totals: TimeLogTotal }>(
    `${URL}/companies/${companyId}/time_periods`,
    payload
  );
};

export const getTimeModulePreferencesRequest = (companyId: string) => {
  return axios.get(`${URL}/companies/${companyId}/preferences`);
};

export const updateTimeModulePreferencesRequest = (companyId: string, countAutoClockOut: boolean) => {
  return axios.put(`${URL}/companies/${companyId}/preferences`, { count_auto_check_out: countAutoClockOut });
};

export const updateClockOutOutsideSettingsRequest = (
  companyId: string,
  countClockOutOutsideSettings: ClockOutOutsideSettings
) => {
  return axios.post(`${URL}/companies/${companyId}/sites/clock_out_settings`, {
    clock_out_settings: countClockOutOutsideSettings,
  });
};

export const getWorkerTimeLogsByShiftRequest = (
  userId: string,
  period: { site_id: string; start_date: string; end_date: string }
) => {
  return axios.post<TimeLogByShift>(`${URL}/users/${userId}/time_logs/time_logs_by_worker_shift`, period);
};

export const getCustomLocationDetailsFields = ({ companyId }: { companyId: string }) => {
  return axios.get<CustomLocationFieldsDetailsDTO>(`${URL}/reports/companies/${companyId}/sites/fields`);
};

export const updateCustomLocationDetailsFields = ({
  companyId,
  data,
}: {
  companyId: string;
  data: { fields: LocationFieldDetailsDTO[] };
}) => {
  return axios.put<CustomLocationFieldsDetailsDTO>(
    `${URL}/reports/companies/${companyId}/sites/fields`,
    data
  );
};

export const getExportingTimeLogsSettings = ({ companyId }: { companyId: string }) => {
  return axios.get<ReportSettingsDTO>(`${URL}/reports/companies/${companyId}/report_settings`);
};

export const updateExportingTimeLogsSettings = ({
  companyId,
  data,
}: {
  companyId: string;
  data: UpdatedReportSettingsData;
}) => {
  return axios.put<ReportSettingsDTO>(`${URL}/reports/companies/${companyId}/report_settings`, data);
};

export const restoreExportingTimeLogsSettings = ({ companyId }: { companyId: string }) => {
  return axios.get<ReportSettingsDTO>(`${URL}/reports/companies/${companyId}/report_settings/restore`);
};

export const getCustomLocationFields = ({ companyId, siteId }: { companyId: string; siteId: string }) => {
  return axios.get<CustomLocationFieldsDTO>(
    `${URL}/reports/companies/${companyId}/sites/${siteId}/fields/values`
  );
};

export const updateCustomLocationFields = ({
  companyId,
  siteId,
  data,
}: {
  companyId: string;
  siteId: string;
  data: { values: Omit<CustomLocationField, 'name'>[] };
}) => {
  return axios.put<CustomLocationFieldsDTO>(
    `${URL}/reports/companies/${companyId}/sites/${siteId}/fields/values`,
    data
  );
};

export const getOnSiteTradesDataRequest = (
  companyId: string,
  payload: { site_ids?: string[] },
  signal: AbortSignal
) => {
  return axios.post<OnSiteTradesData>(
    `${URL}/time_logs/companies/${companyId}/widgets/on_site_trades`,
    payload,
    { signal }
  );
};

export const deleteTimeLogRequest = (companyId: string, workday: string, userId: string) => {
  return axios.delete(`${URL}/companies/${companyId}/workday/${workday}/users/${userId}`);
};

export const getLocationUsersRequest = ({
  companyId,
  data,
  signal,
}: {
  companyId: string;
  data: {
    offset: number;
    limit: number;
    site_ids?: string[];
    status?: string;
    substring?: string;
  };
  signal?: AbortSignal;
}) => {
  return axios.post<{
    count: number;
    items: LocationUserDTO[];
    unique_users_count: number;
    users_statuses: LocationUsersBarchart;
  }>(`${URL}/monitoring/companies/${companyId}/location/workers`, data, { signal });
};

export const getClockedOutRolesRequest = ({
  companyId,
  data,
}: {
  companyId: string;
  data: ClockedOutRolesDataRequest;
}) => {
  return axios.post<{ items: string[] }>(
    `${URL}/monitoring/companies/${companyId}/workers/checked_out/roles`,
    data
  );
};

export const getDidntClockOutRolesRequest = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: ClockedOutRolesDataRequest;
}) => {
  return axios.post<{ items: string[] }>(
    `${URL}/monitoring/companies/${companyId}/workers/did_not_clock_out/roles`,
    payload
  );
};

export const getDidntClockedOutWorkersRequest = ({
  companyId,
  payload,
  signal,
}: {
  companyId: string;
  payload: CheckOutWorkersRequestProps;
  signal?: AbortSignal;
}) => {
  return axios.post<{ count: number; items: CheckOutWorkerDTO[] }>(
    `${URL}/monitoring/companies/${companyId}/workers/did_not_clock_out`,
    payload,
    { signal }
  );
};

export const getWorkerShiftsRequest = ({
  companyId,
  userId,
  siteId,
}: {
  companyId: string;
  userId: string;
  siteId: string;
}) => {
  return axios.post<{ count: number; items: SiteShift[] }>(
    `${URL}/monitoring/companies/${companyId}/users/${userId}/shifts/search`,
    {
      limit: 1000,
      offset: 0,
      order_by: [{ field: 'name', desc: true }],
      site_id: siteId,
    }
  );
};

export const getEmployeeLocationsRequest = ({ userId, payload }: EmployeeLocationsRequest) => {
  return axios.post(`${URL}/users/${userId}/sites/search`, payload);
};

export const removeWorkerFromAllLocationsRequest = (companyId: string, userId: string) => {
  return axios.delete(`${URL}/companies/${companyId}/sites/workers/${userId}`);
};

export const getTeamMembersWithoutRateRequest = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: { limit: number; offset: number; order_by: { field: string; desc: boolean }[] };
}) => {
  return axios.post<{ count: number; items: TeamMembersWithoutRateDTO[] }>(
    `${URL}/monitoring/companies/${companyId}/workers/no_rate`,
    payload
  );
};

export const getCompaniesWorkersWidgetSharedData = <T>({
  companyId,
  target,
  payload,
}: {
  companyId: string;
  target: string;
  payload: T;
}) => {
  return axios.post<string>(
    `${URL}/monitoring/companies/${companyId}/workers/${target}/export_to_xlsx`,
    payload,
    {
      headers: {
        accept: 'application/json',
      },
      responseType: 'blob',
    }
  );
};

export const getCompaniesWidgetsSharedData = <T>({
  companyId,
  target,
  payload,
}: {
  companyId: string;
  target: string;
  payload: T;
}) => {
  return axios.post<string>(
    `${URL}/time_logs/companies/${companyId}/widgets/${target}/export_to_xlsx`,
    payload,
    {
      headers: {
        accept: 'application/json',
      },
      responseType: 'blob',
    }
  );
};

export const getTopPerformingWorkersLastWeekSharedData = ({ companyId }: { companyId: string }) => {
  return axios.get<string>(
    `${URL}/time_logs/companies/${companyId}/widgets/top_performing_workers_last_week/export_to_xlsx`,
    {
      headers: {
        accept: 'application/json',
      },
      responseType: 'blob',
    }
  );
};

export const getCompaniesLocationWorkersSharedData = <T>({
  companyId,
  data,
}: {
  companyId: string;
  data: T;
}) => {
  return axios.post<string>(
    `${URL}/monitoring/companies/${companyId}/location/workers/export_to_xlsx`,
    data,
    {
      headers: {
        accept: 'application/json',
      },
      responseType: 'blob',
    }
  );
};

export const getRecentlyRegisteredRequest = ({
  companyId,
  payload,
  signal,
}: {
  companyId: string;
  payload: {
    offset: number;
    limit: number;
    team_ids?: string[];
  };
  signal?: AbortSignal;
}) => {
  return axios.post<{ count: number; items: RecentlyRegisteredDTO[] }>(
    `${URL}/monitoring/companies/${companyId}/recently_registered_time_users`,
    payload,
    { signal }
  );
};

export const getRecentlyRegisteredSharedData = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: {
    offset: number;
    limit: number;
    team_ids?: string[];
  };
}) => {
  return axios.post<string>(
    `${URL}/monitoring/companies/${companyId}/recently_registered_time_users/export_to_xlsx`,
    payload,
    {
      headers: {
        accept: 'application/json',
      },
      responseType: 'blob',
    }
  );
};

export const getShiftRulesData = ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: {
    offset: number;
    limit: number;
    order_by: TimeOrderByItem[];
  };
}) => {
  return axios.post<{ items: ShiftRuleSchema[]; count: number }>(
    `${URL}/companies/${companyId}/sites/shift_rules/search`,
    payload
  );
};

export const getTimeCandidatesRequest = ({
  companyId,
  teamId,
  payload,
}: GetSyncSettingsData & {
  payload: ModuleCandidatatesSchema;
}) => {
  return axios.post(`${URL}/companies/${companyId}/sites/workers/teams/${teamId}/time_candidates`, payload);
};

export const bulkEditTimeLogsRequest = (payload: BulkEditTimeLogsRequestPayload) => {
  return axios.post(
    `${URL}/time_periods/companies/${payload.company_id}/time_periods/bulk_recalculation`,
    payload
  );
};

export const bulkAmendTimeLogsRequest = ({
  company_id,
  workday,
  site_id,
  shift_id,
  user_id,
  payload,
}: BulkAmendTimeLogsRequest) => {
  return axios.post(
    `${URL}/time_logs/companies/${company_id}/periods/${workday}/sites/${site_id}/shifts/${shift_id}/bulk_amendments?user_id=${user_id}`,
    payload
  );
};

export const getLocationsWithGeoRequest = (companyId: string) => {
  return axios.get(`${URL}/companies/${companyId}/sites/with_geo_position`);
};
